import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useBrand } from '../../context/BrandContext'
import { useAuthContext } from '../../context/AuthContext'
import { useWindowSize, isMobile } from '../../hooks/useWindowSize'
import { useRole, ROLE_LABELS, ROLE_COLORS } from '../../hooks/useRole'

function isLightColor(hex) {
  const h = hex.replace('#','');
  const r = parseInt(h.slice(0,2),16);
  const g = parseInt(h.slice(2,4),16);
  const b = parseInt(h.slice(4,6),16);
  return (r*0.299 + g*0.587 + b*0.114) > 140;
}

export default function AdminLayout() {
  const { profile, signOut } = useAuth()
  const { switchDemoRole, profile: authProfile } = useAuthContext()
  const isDemoMode = authProfile?.demo_mode
  const { brand } = useBrand()
  const navigate = useNavigate()
  const { width } = useWindowSize()
  const mobile = isMobile(width)
  const [menuOpen, setMenuOpen] = useState(false)
  const { can, role, label: roleLabel, color: roleColor } = useRole()

  const accent   = brand.primaryColor || '#B8734A'
  const sidebar  = brand.sidebarColor || '#2B2B2B'
  const bg       = brand.bgColor      || '#F7F2EB'
  const navTextBase = brand.navTextColor || (isLightColor(sidebar) ? '#2B2B2B' : '#FFFFFF')
  const sidebarLight = isLightColor(sidebar)
  const navText  = navTextBase
  const navMuted = navTextBase + '80'
  const navBorder = navTextBase + '20'
  const logoSize = brand.logoSize     || 'medium'
  const logoW    = logoSize === 'large' ? 48 : logoSize === 'small' ? 24 : 34

  const initials = profile
    ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`
    : 'TG'

  async function handleSignOut() {
    await signOut()
    navigate('/login')
  }
  function handleSwitch() {
    const newRole = switchDemoRole()
    navigate(newRole === 'employee' ? '/dashboard' : '/admin', { replace: true })
  }

  const navStyle = ({ isActive }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 18px',
    fontSize: 12,
    color: isActive ? navText : navMuted,
    borderLeft: isActive ? `2px solid ${accent}` : '2px solid transparent',
    background: isActive ? `${accent}22` : 'transparent',
    textDecoration: 'none',
    transition: 'all 0.15s',
  })

  const NavItems = ({ onNav }) => (
    <>
      <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:navMuted,fontWeight:500}}>Overview</div>
      <NavLink to="/admin" end style={navStyle} onClick={onNav}>Dashboard</NavLink>
      {can.viewAllEmployees && <NavLink to="/admin/employees" style={navStyle} onClick={onNav}>Employees</NavLink>}
      {can.viewDeptOnly && (
        <NavLink to="/admin/employees" style={navStyle} onClick={onNav}>
          My Team
          <span style={{marginLeft:'auto',fontSize:9,color:navMuted}}>Dept only</span>
        </NavLink>
      )}

      {(can.approveTime || can.approveTimeOff) && (
        <>
          <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:navMuted,fontWeight:500,marginTop:6}}>Approvals</div>
          <NavLink to="/admin/approvals" style={navStyle} onClick={onNav}>Time Off &amp; Timesheets</NavLink>
        </>
      )}

      {can.viewPerformance && (
        <>
          <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:navMuted,fontWeight:500,marginTop:6}}>Performance</div>
          <NavLink to="/admin/performance" style={navStyle} onClick={onNav}>Performance</NavLink>
        </>
      )}

      {can.sendMemos && (
        <>
          <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:navMuted,fontWeight:500,marginTop:6}}>Content</div>
          <NavLink to="/admin/memos" style={navStyle} onClick={onNav}>Memos</NavLink>
          {can.manageDocuments && <NavLink to="/admin/documents" style={navStyle} onClick={onNav}>Documents</NavLink>}
          {can.editCalendar && <NavLink to="/admin/calendar" style={navStyle} onClick={onNav}>Team Calendar</NavLink>}
          {can.clientConfig && <NavLink to="/admin/config" style={navStyle} onClick={onNav}>Client Config</NavLink>}
      {can.clientConfig && <NavLink to="/admin/roles" style={navStyle} onClick={onNav}>Role Management</NavLink>}
        </>
      )}

      {can.viewHelpdesk && (
        <>
          <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:navMuted,fontWeight:500,marginTop:6}}>Collective Services</div>
          {can.manageVisitors && <NavLink to="/admin/visitors" style={navStyle} onClick={onNav}>Visitor Log</NavLink>}
          <NavLink to="/admin/helpdesk" style={navStyle} onClick={onNav}>
            Helpdesk
            <span style={{marginLeft:'auto',background:'#A0392B',color:'#fff',fontSize:9,padding:'1px 5px',borderRadius:8,fontWeight:600}}>2</span>
          </NavLink>
          {can.devRequests && <NavLink to="/admin/dev-requests" style={navStyle} onClick={onNav}>Dev Requests</NavLink>}
      {can.viewHelpdesk && <NavLink to="/admin/it-changes" style={navStyle} onClick={onNav}>IT Change Mgmt</NavLink>}
      {can.viewHelpdesk && <NavLink to="/admin/it-worklog" style={navStyle} onClick={onNav}>IT Work Log</NavLink>}
        </>
      )}

      {brand.features?.roomScheduler && (
        <>
          <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:navMuted,fontWeight:500,marginTop:6}}>{brand.campusLabel || 'Collective Campus'}</div>
          <NavLink to="/admin/campus" style={navStyle} onClick={onNav}>Booking Grid</NavLink>
        </>
      )}
    </>
  )

  const UserSection = () => (
    <div style={{padding:'14px 18px',borderBottom:'1px solid '+navBorder}}>
      <div style={{width:38,height:38,borderRadius:'50%',background:`${accent}25`,border:`1.5px solid ${accent}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:600,color:accent,marginBottom:7}}>{initials}</div>
      <div style={{fontSize:13,color:navText,fontWeight:500}}>{profile?.first_name} {profile?.last_name}</div>
      <div style={{display:'flex',alignItems:'center',gap:6,marginTop:3}}>
        <div style={{width:6,height:6,borderRadius:'50%',background:roleColor,flexShrink:0}}></div>
        <div style={{fontSize:10,color:roleColor,textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:600}}>{roleLabel}</div>
      </div>
      {profile?.department && <div style={{fontSize:11,color:navMuted,marginTop:2}}>{profile.department}</div>}
      {(isDemoMode || profile) && (
        <button onClick={handleSwitch} style={{marginTop:8,padding:'4px 10px',background:'none',border:'1px solid rgba(184,115,74,0.27)',borderRadius:4,fontSize:10,color:'rgba(184,115,74,0.6)',cursor:'pointer',fontFamily:"'Montserrat',sans-serif",textAlign:'left'}}>
          Employee View &rarr;
        </button>
      )}
    </div>
  )

  if (mobile) {
    return (
      <div style={{display:'flex',flexDirection:'column',minHeight:'100vh',background:bg,fontFamily:"'Montserrat',sans-serif"}}>
        <div style={{background:sidebar,padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:200,flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <button onClick={()=>setMenuOpen(o=>!o)} style={{background:'none',border:'none',cursor:'pointer',padding:4,display:'flex',flexDirection:'column',gap:4}}>
              <div style={{width:20,height:2,background:menuOpen?accent:'rgba(255,255,255,0.6)',borderRadius:1,transition:'all 0.2s',transform:menuOpen?'rotate(45deg) translate(4px,4px)':'none'}}></div>
              <div style={{width:20,height:2,background:'rgba(255,255,255,0.6)',borderRadius:1,transition:'all 0.2s',opacity:menuOpen?0:1}}></div>
              <div style={{width:20,height:2,background:menuOpen?accent:'rgba(255,255,255,0.6)',borderRadius:1,transition:'all 0.2s',transform:menuOpen?'rotate(-45deg) translate(4px,-4px)':'none'}}></div>
            </button>
            <div style={{fontFamily:'Georgia,serif',fontSize:13,color:'#fff',fontWeight:600}}>Collective Staff</div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            {(isDemoMode||profile) && (
              <button onClick={handleSwitch} style={{fontSize:9,padding:'4px 8px',background:'none',border:`1px solid rgba(255,255,255,0.2)`,borderRadius:4,color:navMuted,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
                Employee View
              </button>
            )}
            <div style={{width:30,height:30,borderRadius:'50%',background:`${accent}25`,border:`1.5px solid ${accent}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:600,color:accent}}>{initials}</div>
          </div>
        </div>
        {menuOpen && (
          <div style={{position:'fixed',inset:0,zIndex:150}} onClick={()=>setMenuOpen(false)}>
            <div style={{position:'absolute',top:52,left:0,bottom:0,width:260,background:sidebar,overflowY:'auto',boxShadow:'4px 0 20px rgba(0,0,0,0.3)'}} onClick={e=>e.stopPropagation()}>
              <div style={{padding:'14px 18px',borderBottom:'1px solid '+navBorder}}>
                <div style={{fontSize:13,color:navText,fontWeight:500}}>{profile?.first_name} {profile?.last_name}</div>
                <div style={{display:'flex',alignItems:'center',gap:6,marginTop:3}}>
                  <div style={{width:6,height:6,borderRadius:'50%',background:roleColor}}></div>
                  <div style={{fontSize:10,color:roleColor,textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:600}}>{roleLabel}</div>
                </div>
              </div>
              <nav style={{padding:'10px 0'}}>
                <NavItems onNav={()=>setMenuOpen(false)}/>
              </nav>
              <div style={{padding:'12px 18px',borderTop:'1px solid '+navBorder}}>
                <button onClick={handleSignOut} style={{background:'none',border:'none',fontSize:11,color:navMuted,cursor:'pointer',padding:0,fontFamily:"'Montserrat',sans-serif"}}>Sign out</button>
              </div>
            </div>
          </div>
        )}
        <main style={{flex:1,padding:'16px',color:brand.textColor||'#2B2B2B'}}>
          <Outlet />
        </main>
      </div>
    )
  }

  return (
    <div style={{display:'grid',gridTemplateColumns:'220px 1fr',minHeight:'100vh',fontFamily:"'Montserrat',sans-serif"}}>
      <aside style={{background:sidebar,display:'flex',flexDirection:'column',position:'sticky',top:0,height:'100vh',overflowY:'auto'}}>
        <div style={{padding:'20px 18px 14px',borderBottom:'1px solid '+navBorder}}>
          <div style={{display:'flex',alignItems:'center',gap:'10px',cursor:'pointer'}} onClick={()=>navigate('/admin')}>
            {brand.logoUrl
              ? <img src={brand.logoUrl} alt="logo" style={{width:logoW,height:logoW,objectFit:'contain',borderRadius:4}}/>
              : <div style={{width:34,height:34,borderRadius:'50%',border:`1px solid ${accent}`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'Georgia,serif',fontSize:12,fontWeight:700,color:accent}}>{brand.monogram || 'CS'}</div>
            }
            <div>
              <div style={{fontFamily:'Georgia,serif',fontSize:14,color:'#fff',fontWeight:600,lineHeight:1}}>Collective Staff</div>
              <div style={{fontSize:9,color:navMuted,letterSpacing:'0.12em',textTransform:'uppercase',marginTop:2}}>{brand.companyName || 'Demo Company'}</div>
            </div>
          </div>
        </div>
        <UserSection />
        <nav style={{flex:1,padding:'10px 0'}}>
          <NavItems onNav={null}/>
        </nav>
        <div style={{padding:'12px 18px',borderTop:'1px solid '+navBorder}}>
          {(isDemoMode || profile) && (
            <button onClick={handleSwitch} style={{display:'block',width:'100%',marginBottom:8,padding:'5px 8px',background:'none',border:'1px solid '+navBorder,borderRadius:4,fontSize:10,color:navMuted,cursor:'pointer',fontFamily:"'Montserrat',sans-serif",textAlign:'left'}}>
              Switch to Employee Portal
            </button>
          )}
          <button onClick={handleSignOut} style={{background:'none',border:'none',fontSize:11,color:navMuted,cursor:'pointer',padding:0,fontFamily:"'Montserrat',sans-serif"}}>
            Sign out
          </button>
          <div style={{fontSize:10,color:navMuted,marginTop:4}}>gallaghercollectiveworks.com</div>
        </div>
      </aside>
      <main style={{display:'flex',flexDirection:'column',minHeight:'100vh',background:bg}}>
        <div style={{flex:1,padding:'24px',color:brand.textColor||'#2B2B2B'}}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}
